import { createContext, useEffect, useRef, useState } from 'react';
import { matchPath, withRouter, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { IonRouterLink, IonSpinner, IonButton, IonPopover, IonIcon } from '@ionic/react';
import { ellipsisHorizontal } from 'ionicons/icons';

import { appUpdateRequired } from 'helpers/project-schema';

import ProjectActions from 'actions/ProjectActions';

import { userInfoSelector } from 'selectors/userSelector';
import { projectByIdSelector, currentProjectIDSelector } from 'selectors/projectSelector';

import NavBar from 'components/layouts/helpers/navbar';
import ProjectMenu from 'components/project/ProjectMenu';
import MainLayout from 'components/layouts/MainLayout';

export const ProjectContext = createContext({});

const normalizeId = (id) => parseInt(id, 10) == id ? parseInt(id, 10) : id;

const loadingScreen = (
  <div className="loading-container visible active">
    <div className="loading">
      <IonSpinner name="lines-sharp"/>
    </div>
  </div>
);

function ProjectLayout({InnerComponent, modal=false, match}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const {params: {id: idStr, segment}} = matchPath(match.url, {path: '/projects/:id/:segment?'});
  const id = normalizeId(idStr);
  const [errorMsg, setErrorMsg] = useState();

  const project = useSelector(projectByIdSelector(id));

  const currentProjectID = useSelector(currentProjectIDSelector);
  const user = useSelector(userInfoSelector);
  const projectRequest = useRef(Promise.resolve());

  useEffect(() => {
    projectRequest.current = projectRequest.current.finally(async () => {
      if (currentProjectID !== id || !project) {
        try {
          const {id:projectId, errors} = await dispatch(ProjectActions.setCurrentProject(id)) ?? {};
          if (!projectId && errors) {
            setErrorMsg(errors?.error ?? errors?.errors);
          }
        } catch(e) {
          setErrorMsg(e.toString());
        }
      };
    });
  }, [id]);

  const {owned, name} = project ?? {};
  const readonly = !project?.owned && !user.global_editor;
  const projectLoaded = project && name && user;
  const projectTitle = projectLoaded && project?.name || (errorMsg ? "error" : "loading");

  if (project?.idWas == id) {
    history.replace(match.url.replace(id, project?.id));
  }

  if (projectLoaded && appUpdateRequired(project)) {
    return (
      <MainLayout
        title={projectTitle}
        parentName={owned !== false ? "Projects" : "Global Projects"}
        parentPath={owned !== false ? "/projects" : "/global-projects"}
      >
        <div className="padding">
          <p className="error">
            This project was created with a newer version of GeoApp. Please update to view.
          </p>
        </div>
      </MainLayout>
    );
  }

  if (modal) {
    return (
      <ProjectContext.Provider value={project}>
        {errorMsg && <div className="ion-padding"><p className="error">{errorMsg}</p></div>}
        {projectLoaded ? (
          <InnerComponent key={project?.id} user={user} readonly={readonly}/>
        ) : (
          !errorMsg && loadingScreen
        )}
      </ProjectContext.Provider>
    );
  }

  return (
    <ProjectContext.Provider value={project}>
      <MainLayout
        title={
          <IonRouterLink routerLink={`/projects/${id}/settings`} color="light">
            {projectTitle}
          </IonRouterLink>
        }
        parentName={owned !== false ? "Projects" : "Global Projects"}
        parentPath={owned !== false ? "/projects" : "/global-projects"}
        primaryButton={projectLoaded && <>
          <IonButton fill="clear" id={`project-menu-${segment}`} title="Project Menu">
            <IonIcon icon={ellipsisHorizontal} size="large" slot="end"/>
          </IonButton>
          <IonPopover trigger={`project-menu-${segment}`} dismissOnSelect triggerAction="click">
            <ProjectMenu project={project} setErrorMsg={setErrorMsg}/>
          </IonPopover>
        </>}
        navBar={projectLoaded && (
          <NavBar pathPrefix={`/projects/${id}/`} tabs={[
            {title: "Loads", path: "loads"},
            {title: "Soil", path: "soil"},
            {title: "Pile", path: "pile"},
            {title: "Results", path: "results"},
            {title: "Logs", path: "logs"}
          ]}/>
        )}
      >
        {errorMsg && <div className="ion-padding"><p className="error">{errorMsg}</p></div>}
        {projectLoaded ? (
          <InnerComponent key={project?.id} user={user} readonly={readonly}/>
        ) : (
          !errorMsg && loadingScreen
        )}
      </MainLayout>
    </ProjectContext.Provider>
  );
};

export default withRouter(ProjectLayout)
